import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Breakpoints, Color, Grid, Type } from '../../../app/StyleGuide';
import { PrimaryButton } from '../../../components/Buttons';
import {
  BillingPeriod,
  CurriculumPricingEntity,
  planDetails,
  PlanDetailsSection,
  PlanType,
  Subscription,
} from '../../curriculum';
import { SelectedCurriculum } from '../SwitchCurriculum';
import { buildBasicPlusFeaturesFromSections } from '../utils/planFeatureUtils';
import { SmallTag } from './SmallTag';

const Container = styled.div`
  width: 100%;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: ${Grid._4};
  gap: ${Grid._4};
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  min-width: 280px;
  height: 45px;
  cursor: pointer;

  @media screen and (max-width: ${Breakpoints.screen_sm}) {
    min-width: 100%;
    max-width: 100%;
  }
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span:before {
    transform: translateX(100%);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${Color.Gray._20};
  transition: all 0.4s ease;
  border-radius: ${Grid._3};
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  padding: ${Grid._2};

  &:before {
    position: absolute;
    content: '';
    height: 37px;
    width: calc(50% - ${Grid._2});
    left: ${Grid._2};
    bottom: ${Grid._2};
    background-color: ${Color.white};
    transition: all 0.4s ease;
    border-radius: ${Grid._2};
    border: 1px solid ${Color.Gray._50};
    z-index: 1;
  }
`;

const ToggleText = styled.span<{ isActive?: boolean }>`
  position: relative;
  z-index: 2;
  font-size: ${Type.Scale._2};
  font-weight: ${Type.Weight.medium};
  text-align: center;
  transition: color 0.4s ease;
  padding: 0 ${Grid._3};
  white-space: nowrap;

  span {
    font-size: ${Type.Scale._1};
    color: ${Color.Green._50};
  }
`;

const CardsGrid = styled.div<{ columns: 1 | 2 }>`
  display: grid;
  grid-template-columns: ${props => (props.columns === 2 ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)')};
  gap: ${Grid._5};
  width: 100%;

  @media screen and (max-width: ${Breakpoints.screen_sm}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const SubscriptionPlanCard = styled.div<{ bgColor: string; isSelected?: boolean }>`
  background-color: ${props => props.bgColor};
  border: 1px solid ${Color.Gray._30};
  border-radius: ${Grid._3};
  padding: ${Grid._5};
  position: relative;
  max-width: 650px;

  ${props =>
    props.isSelected
      ? `
    &::after {
      content: '';
      position: absolute;
      inset: -1px;
      border: 2px solid ${Color.Blue._50};
      border-radius: ${Grid._3};
      pointer-events: none;
    }
  `
      : ''}
`;

const CardHeader = styled.div`
  position: relative;
  padding-bottom: ${Grid._4};
  margin-bottom: ${Grid._4};
  border-bottom: 1px solid ${Color.Gray._30};

  button {
    margin-top: ${Grid._4};
    width: 100%;
  }
`;

const PopularTag = styled(SmallTag)`
  @media screen and (max-width: ${Breakpoints.screen_sm}) {
    top: -${Grid._3};
    right: ${Grid._3};
  }
`;

const PlusPlanHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Grid._3};
`;

const PlanName = styled.h3`
  margin: 0 0 ${Grid._2} 0;
  font-weight: ${Type.Weight.semibold};
  font-size: ${Type.Scale._5};
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: flex-start;
  gap: ${Grid._3};
  flex-wrap: wrap;

  p {
    font-size: ${Type.Scale._5};
    font-weight: ${Type.Weight.semibold};
    margin: 0;
  }

  span {
    font-size: ${Type.Scale._3};
    font-weight: ${Type.Weight.normal};
    color: ${Color.Gray._50};
  }
`;

const AnnualBillingText = styled.p`
  margin: ${Grid._2} 0 0 0;
  font-size: ${Type.Scale._3};
  font-weight: ${Type.Weight.normal};
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: ${Grid._3};
`;

const FeatureItem = styled.li`
  display: grid;
  grid-template-columns: 18px 1fr;
  align-items: start;
  gap: ${Grid._3};
  font-size: ${Type.Scale._3};
`;

const Checkmark = styled.span`
  color: ${Color.Primary._50};
  font-weight: ${Type.Weight.bold};
  line-height: 1.2;
`;

const PlanButton = styled(PrimaryButton)`
  span {
    font-size: ${Type.Scale._2};
    font-weight: ${Type.Weight.normal};
    color: ${Color.Gray._10};
  }
`;

const usdPriceFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const planConfig: Record<PlanType, { label: string; bgColor: string; showPopularTag: boolean }> = {
  basic: { label: 'Basic Plan', bgColor: Color.Gray._05, showPopularTag: false },
  plus: { label: 'Plus Plan', bgColor: Color.Gray._10, showPopularTag: true },
};

type PlanCardComponentProps = {
  label: string;
  bgColor: string;
  showPopularTag: boolean;
  billingPeriod: BillingPeriod;
  selectedPlan: PlanType;
  hasMultiplePlans: boolean;
  currentSelectedPeriod: BillingPeriod;
  currentSelectedPackage: PlanType;
  plan: PlanType;
  planPrices: { monthly: number | string; yearly: number | string };
  handlePlanSelect: (plan: PlanType, billingPeriod: BillingPeriod) => void;
  getPlanFeatures: (plan: PlanType) => string[];
};

const formatPrice = (price: number | string) => usdPriceFormatter.format(Number(price));

const PlanCard = ({
  label,
  bgColor,
  showPopularTag,
  billingPeriod,
  selectedPlan,
  hasMultiplePlans,
  currentSelectedPeriod,
  currentSelectedPackage,
  plan,
  planPrices,
  handlePlanSelect,
  getPlanFeatures,
}: PlanCardComponentProps) => {
  const monthlyDisplay = billingPeriod === 'year' ? Number(planPrices.yearly) / 12 : Number(planPrices.monthly);
  const isSelected = plan === 'plus' ? selectedPlan === 'plus' : selectedPlan === 'basic' && hasMultiplePlans;
  const isCurrentPlan = currentSelectedPeriod === billingPeriod && currentSelectedPackage === plan;
  const showAnnualBilling = billingPeriod === 'year' && planPrices.yearly;

  const headerContent = (
    <>
      <PlanName>{label}</PlanName>
      {showPopularTag && (
        <PopularTag>
          <i className="fa fa-solid fa-star" aria-hidden /> Most Popular
        </PopularTag>
      )}
    </>
  );

  return (
    <SubscriptionPlanCard bgColor={bgColor} isSelected={isSelected} aria-label={`${label.toLowerCase()} card`}>
      <CardHeader>
        {showPopularTag ? <PlusPlanHeader>{headerContent}</PlusPlanHeader> : headerContent}
        <PriceContainer>
          <p>
            {formatPrice(monthlyDisplay)} <span>per month</span>
          </p>
        </PriceContainer>
        {showAnnualBilling && (
          <AnnualBillingText>Billed Annually at {formatPrice(planPrices.yearly)}</AnnualBillingText>
        )}
        {hasMultiplePlans && (
          <PlanButton onClick={() => handlePlanSelect(plan, billingPeriod)}>
            Select {isCurrentPlan && <span>(Current Subscription)</span>}
          </PlanButton>
        )}
      </CardHeader>
      <FeaturesList aria-label={`${label} features`}>
        {getPlanFeatures(plan).map((name, idx) => (
          <FeatureItem key={`${name}-${idx}`}>
            <Checkmark aria-hidden>✓</Checkmark>
            <span>{name}</span>
          </FeatureItem>
        ))}
      </FeaturesList>
    </SubscriptionPlanCard>
  );
};

type BasicPlusPlanCardsProps = {
  selectedCurriculum: SelectedCurriculum;
  selectedMinistrySize: CurriculumPricingEntity;
  currentSelectedPeriod: BillingPeriod;
  currentSelectedPackage: PlanType;
  onPlanSelect: (subscription: Subscription) => void;
};

const BasicPlusPlanCards = ({
  selectedCurriculum,
  selectedMinistrySize,
  currentSelectedPeriod,
  currentSelectedPackage,
  onPlanSelect,
}: BasicPlusPlanCardsProps) => {
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('year');
  const [selectedPlan, setSelectedPlan] = useState<PlanType>();

  const featureList = useMemo(() => {
    const brand = planDetails[selectedCurriculum.brand.code];
    const ageGroup = selectedCurriculum?.ageGroup?.name?.toLowerCase?.() || '';

    let sections: PlanDetailsSection[] = [];

    if (ageGroup.includes('preschool') && ageGroup.includes('kid') && brand?.['preschool and kids']) {
      sections = brand['preschool and kids'];
    } else if (ageGroup.includes('kid') && brand?.kids) {
      sections = brand.kids;
    } else if (ageGroup.includes('preschool') && brand?.preschool) {
      sections = brand.preschool;
    } else if (ageGroup.includes('student') && brand?.students) {
      sections = brand.students;
    } else if (ageGroup.includes('adult') && brand?.adult) {
      sections = brand.adult;
    }

    return buildBasicPlusFeaturesFromSections(Array.isArray(sections) ? sections : []);
  }, [selectedCurriculum]);

  const allFeaturesSameType = featureList.length > 0 && featureList.every(f => f.type === featureList[0].type);

  const availablePlans: PlanType[] = useMemo(() => {
    if (allFeaturesSameType && featureList.length > 0) return [featureList[0].type];
    return ['basic', 'plus'];
  }, [allFeaturesSameType, featureList]);

  const getSubscriptionPrice = (plan: PlanType, period: 'month' | 'year') =>
    selectedMinistrySize?.subscription.find(
      s => s.package.toLowerCase().includes(plan) && s.period.toLowerCase().includes(period)
    )?.price || 0;

  const prices = useMemo<Record<PlanType, { monthly: string | number; yearly: string | number }>>(() => {
    return {
      basic: { monthly: getSubscriptionPrice('basic', 'month'), yearly: getSubscriptionPrice('basic', 'year') },
      plus: { monthly: getSubscriptionPrice('plus', 'month'), yearly: getSubscriptionPrice('plus', 'year') },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMinistrySize]);

  const getYearlySavingsPercentage = (plan: PlanType) => {
    const monthly = Number(prices[plan].monthly);
    const yearly = Number(prices[plan].yearly);
    if (!monthly || !yearly) return '0';
    const annualMonthlyTotal = monthly * 12;
    if (annualMonthlyTotal <= 0) return '0';
    return (((annualMonthlyTotal - yearly) / annualMonthlyTotal) * 100).toFixed(0);
  };

  const toggleSavingsPlan: PlanType = availablePlans.length === 1 ? availablePlans[0] : 'basic';
  const yearlySavingsPercentage = getYearlySavingsPercentage(toggleSavingsPlan);

  const handlePlanSelect = (plan: PlanType, nextBillingPeriod: BillingPeriod) => {
    setSelectedPlan(plan);
    const selectedBillingPeriod = nextBillingPeriod === 'year' ? 'year' : 'month';
    const subscription = selectedMinistrySize?.subscription.find(
      s => s.package.toLowerCase().includes(plan) && s.period.toLowerCase().includes(selectedBillingPeriod)
    );

    if (subscription) onPlanSelect(subscription);
  };

  const handleBillingToggle = () => {
    const newPeriod: BillingPeriod = billingPeriod === 'year' ? 'month' : 'year';
    const selectedBillingPeriod = newPeriod === 'year' ? 'year' : 'month';

    setBillingPeriod(newPeriod);
    const subscription = selectedMinistrySize?.subscription.find(
      s =>
        s.package.toLowerCase().includes(selectedPlan || '') && s.period.toLowerCase().includes(selectedBillingPeriod)
    );
    if (subscription) onPlanSelect(subscription);
  };

  useEffect(() => {
    if (allFeaturesSameType && featureList.length > 0) {
      const featureType = featureList[0].type;
      setSelectedPlan(featureType);
      const selectedBillingPeriod = billingPeriod === 'year' ? 'year' : 'month';
      const subscription = selectedMinistrySize?.subscription.find(
        s => s.package.toLowerCase().includes(featureType) && s.period.toLowerCase().includes(selectedBillingPeriod)
      );

      if (subscription) onPlanSelect(subscription);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFeaturesSameType, selectedCurriculum, selectedMinistrySize, billingPeriod, onPlanSelect]);

  const getPlanFeatures = (plan: PlanType) => {
    if (plan === 'plus') return featureList.map(f => f.name);
    return featureList.filter(f => f.type === 'basic').map(f => f.name);
  };

  return (
    <Container>
      <ToggleContainer>
        <ToggleSwitch>
          <ToggleInput type="checkbox" checked={billingPeriod === 'year'} onChange={handleBillingToggle} />
          <ToggleSlider>
            <ToggleText isActive={billingPeriod === 'month'}>Monthly</ToggleText>
            <ToggleText isActive={billingPeriod === 'year'}>
              Yearly <span>(save {yearlySavingsPercentage}%)</span>
            </ToggleText>
          </ToggleSlider>
        </ToggleSwitch>
      </ToggleContainer>

      <CardsGrid columns={availablePlans.length === 2 ? 2 : 1} aria-label="Basic and Plus plan comparison">
        {availablePlans.map(plan => {
          const config = planConfig[plan];
          return (
            <PlanCard
              key={plan}
              {...config}
              planPrices={prices[plan]}
              billingPeriod={billingPeriod}
              selectedPlan={selectedPlan as PlanType}
              hasMultiplePlans={availablePlans.length === 2}
              currentSelectedPeriod={currentSelectedPeriod}
              currentSelectedPackage={currentSelectedPackage}
              plan={plan}
              handlePlanSelect={handlePlanSelect}
              getPlanFeatures={getPlanFeatures}
            />
          );
        })}
      </CardsGrid>
    </Container>
  );
};

export default BasicPlusPlanCards;
